/**
 * /try Client Media Transcoding Pipeline.
 *
 * WHAT THIS FILE DOES:
 * - Transcodes browser-uploaded audio/video to lightweight MP3 (`audio/mpeg`).
 * - Uses Web-Demuxer + WebCodecs AudioDecoder for client-side extraction/decoding.
 * - Encodes mono 16kHz PCM frames to MP3 for Gemini Files API ingest.
 * - Applies deterministic timeout + size guardrails and returns typed fallback reasons.
 *
 * WHY THIS EXISTS:
 * - Phase 1 of /try client-audio-first ingest requires a dedicated media pipeline
 *   that is fully decoupled from UI code.
 * - Keeping transcoding in a focused module avoids turning try-client.ts / try-ui.ts
 *   into god modules.
 *
 * CALLED BY:
 * - Phase 2 integration will call transcodeTryMediaClientSide() from try-client.ts.
 *
 * THIS FILE CALLS:
 * - web-demuxer (WASM demuxing)
 * - WebCodecs AudioDecoder (browser native decode)
 * - @breezystack/lamejs (MP3 encoding)
 * - try-utils media profiling + byte accounting helpers
 *
 * OUTPUT CONTRACT:
 * - Success: File with `type: 'audio/mpeg'`
 * - Failure: Typed fallback reason so caller can route to server extraction path
 *
 * NON-GOALS:
 * - UI status rendering (handled in try-ui.ts)
 * - Upload orchestration (handled in try-client.ts)
 *
 * @file src/client/try-media-transcode.ts
 */

import {
  TryClientAudioTranscodeFormat,
  TryMediaIngestLimits,
  TryMediaTranscodeFallbackReasons,
  type TryClientAudioTranscodeConfig,
  type TryMediaBootstrapConfig,
  type TryMediaTranscodeFallbackReason,
  WebTryError,
  resolveMediaTranscodeFallbackReason,
  resolveTryMediaBootstrapConfig
} from './try-shared.js';
import {
  computeMediaByteAccounting,
  estimateMp3OutputBytes,
  profileMediaFile,
  resolveMediaContentType,
  type MediaByteAccounting,
  type MediaFileProfile
} from './try-utils.js';

interface Mp3EncoderInstance {
  encodeBuffer(left: Int16Array, right?: Int16Array): Uint8Array;
  flush(): Uint8Array;
}

type Mp3EncoderConstructor = new (
  channels: number,
  sampleRate: number,
  kbps: number
) => Mp3EncoderInstance;

interface WebDemuxerInstance {
  load(file: File): Promise<void>;
  getDecoderConfig(type: 'audio' | 'video'): Promise<AudioDecoderConfig>;
  read(type: 'audio' | 'video'): ReadableStream<EncodedAudioChunk>;
  destroy(): void;
}

type WebDemuxerConstructor = new (options?: Record<string, unknown>) => WebDemuxerInstance;

interface TranscodeDependencies {
  Mp3Encoder: Mp3EncoderConstructor;
  WebDemuxer: WebDemuxerConstructor;
}

/**
 * Lazily resolved vendor dependencies for client transcoding.
 *
 * Why lazy:
 * - `@breezystack/lamejs` and `web-demuxer` are browser-only dependencies.
 * - Importing them at module load breaks Node test/runtime paths that only need
 *   to import the module without executing the transcode pipeline.
 */
let transcodeDependenciesPromise: Promise<TranscodeDependencies> | null = null;

/**
 * Loads browser-only transcode vendor modules on demand.
 */
async function loadTranscodeDependencies(): Promise<TranscodeDependencies> {
  if (!transcodeDependenciesPromise) {
    transcodeDependenciesPromise = (async () => {
      try {
        const [lameModule, demuxerModule] = await Promise.all([
          import('@breezystack/lamejs'),
          import('web-demuxer')
        ]);

        const Mp3Encoder = (lameModule as { Mp3Encoder?: unknown }).Mp3Encoder;
        const WebDemuxer = (demuxerModule as { WebDemuxer?: unknown }).WebDemuxer;

        if (typeof Mp3Encoder !== 'function' || typeof WebDemuxer !== 'function') {
          throw new WebTryError('media_transcode_unsupported_api', {
            details: 'Transcode vendor exports are unavailable.'
          });
        }

        return {
          Mp3Encoder: Mp3Encoder as Mp3EncoderConstructor,
          WebDemuxer: WebDemuxer as WebDemuxerConstructor
        };
      } catch (error) {
        throw new WebTryError('media_transcode_unsupported_api', { details: error });
      }
    })();
  }

  return await transcodeDependenciesPromise;
}

const TranscodeQueueConfig = Object.freeze({
  /**
   * Decoder queue upper bound before we backpressure demux reading.
   */
  MAX_DECODE_QUEUE_SIZE: 24,
  /**
   * Sleep interval while waiting for decoder queue to drain.
   */
  BACKPRESSURE_WAIT_MS: 4,
  /**
   * MP3 frame size used by lamejs.
   */
  MP3_FRAME_SAMPLES: 1152
} as const);

/**
 * Scale factors for Float32-to-Int16 PCM conversion.
 *
 * IEEE 754 float PCM uses [-1.0, 1.0]. Int16 PCM uses [-32768, 32767].
 * Negative and positive ranges are asymmetric, so we use separate constants.
 */
const Int16ScaleFactors = Object.freeze({
  /** Multiplier for negative float samples: -1.0 * 0x8000 = -32768 (Int16 min). */
  NEGATIVE: 0x8000,
  /** Multiplier for positive float samples: 1.0 * 0x7FFF = 32767 (Int16 max). */
  POSITIVE: 0x7FFF
} as const);

const TranscodeAssets = Object.freeze({
  /**
   * Static asset path for web-demuxer full WASM build.
   *
   * build.js copies this asset from node_modules into dist/static/vendor.
   */
  WEB_DEMUXER_WASM_PATH: '/static/vendor/web-demuxer/web-demuxer.wasm'
} as const);

interface TranscodePipelineOutput {
  blob: Blob;
  durationMs: number | null;
}

interface TranscodeRuntime {
  profileMediaFile: typeof profileMediaFile;
  executePipeline: (params: {
    file: File;
    config: TryClientAudioTranscodeConfig;
    signal: AbortSignal;
  }) => Promise<TranscodePipelineOutput>;
  now: () => number;
  scheduleTimeout: (callback: () => void, ms: number) => ReturnType<typeof setTimeout>;
  clearScheduledTimeout: (timerId: ReturnType<typeof setTimeout>) => void;
}

const DefaultRuntime: TranscodeRuntime = {
  profileMediaFile,
  executePipeline: executeWebCodecsTranscodePipeline,
  now: () => Date.now(),
  scheduleTimeout: (callback: () => void, ms: number) => setTimeout(callback, ms),
  clearScheduledTimeout: (id) => clearTimeout(id)
};

export interface TryMediaTranscodeSuccess {
  status: 'success';
  transcodedFile: File;
  fallbackReason: null;
  profile: MediaFileProfile;
  byteAccounting: MediaByteAccounting;
  durationMs: number | null;
}

export interface TryMediaTranscodeFallback {
  status: 'fallback';
  transcodedFile: null;
  fallbackReason: TryMediaTranscodeFallbackReason;
  profile: MediaFileProfile | null;
  byteAccounting: null;
  durationMs: number | null;
  errorMessage: string | null;
}

export type TryMediaTranscodeResult = TryMediaTranscodeSuccess | TryMediaTranscodeFallback;

/**
 * Resolves the static WASM path used by web-demuxer.
 */
export function resolveTryMediaDemuxerWasmPath(): string {
  return TranscodeAssets.WEB_DEMUXER_WASM_PATH;
}

/**
 * Transcodes media to client-side MP3 when supported.
 *
 * Returns a typed fallback reason instead of throwing so callers can
 * transparently route to the server extraction path.
 */
export async function transcodeTryMediaClientSide(
  params: {
    file: File;
    mediaConfig?: TryMediaBootstrapConfig | null;
    signal?: AbortSignal;
  },
  runtimeOverrides: Partial<TranscodeRuntime> = {}
): Promise<TryMediaTranscodeResult> {
  const runtime = { ...DefaultRuntime, ...runtimeOverrides };
  const mediaConfig = resolveTryMediaBootstrapConfig(
    params.mediaConfig ?? readWindowTryMediaConfig()
  );
  const transcodeConfig = mediaConfig.clientAudioTranscode;

  if (!transcodeConfig.enabled) {
    return buildFallback({
      reason: TryMediaTranscodeFallbackReasons.DISABLED,
      profile: null,
      durationMs: null,
      error: null
    });
  }

  if (!isClientTranscodeSupported()) {
    return buildFallback({
      reason: TryMediaTranscodeFallbackReasons.UNSUPPORTED_API,
      profile: null,
      durationMs: null,
      error: new WebTryError('media_transcode_unsupported_api')
    });
  }

  const contentType = resolveMediaContentType(params.file);
  if (!contentType) {
    return buildFallback({
      reason: TryMediaTranscodeFallbackReasons.UNSUPPORTED_MEDIA_TYPE,
      profile: {
        fileName: params.file.name,
        contentType: null,
        fileSizeBytes: params.file.size,
        durationMs: null,
        mediaKind: 'unknown'
      },
      durationMs: null,
      error: new WebTryError('media_transcode_unsupported_media_type')
    });
  }

  if (params.file.size > transcodeConfig.maxInputBytes) {
    return buildFallback({
      reason: TryMediaTranscodeFallbackReasons.INPUT_TOO_LARGE,
      profile: {
        fileName: params.file.name,
        contentType,
        fileSizeBytes: params.file.size,
        durationMs: null,
        mediaKind: contentType.startsWith('audio/') ? 'audio' : 'video'
      },
      durationMs: null,
      error: new WebTryError('media_transcode_input_too_large')
    });
  }

  let profile: MediaFileProfile | null = null;

  try {
    profile = await runtime.profileMediaFile(params.file);

    if (profile.durationMs !== null && profile.durationMs > TryMediaIngestLimits.MAX_MEDIA_DURATION_MS) {
      throw new WebTryError('media_duration_too_long');
    }

    if (profile.durationMs !== null) {
      const estimatedOutputBytes = estimateMp3OutputBytes(
        profile.durationMs,
        TryClientAudioTranscodeFormat.OUTPUT_BITRATE_KBPS
      );
      if (estimatedOutputBytes > transcodeConfig.maxOutputBytes) {
        throw new WebTryError('media_transcode_output_too_large');
      }
    }

    const pipelineOutput = await executeWithTimeout(
      (signal) => runtime.executePipeline({
        file: params.file,
        config: transcodeConfig,
        signal
      }),
      {
        timeoutMs: transcodeConfig.timeoutMs,
        externalSignal: params.signal,
        runtime
      }
    );

    if (pipelineOutput.blob.size > transcodeConfig.maxOutputBytes) {
      throw new WebTryError('media_transcode_output_too_large');
    }

    const transcodedFile = new File(
      [pipelineOutput.blob],
      buildTranscodedFileName(params.file.name),
      {
        type: TryClientAudioTranscodeFormat.OUTPUT_MIME_TYPE,
        lastModified: runtime.now()
      }
    );

    const byteAccounting = computeMediaByteAccounting(params.file.size, transcodedFile.size);

    return {
      status: 'success',
      transcodedFile,
      fallbackReason: null,
      profile,
      byteAccounting,
      durationMs: pipelineOutput.durationMs
    };
  } catch (error) {
    return buildFallback({
      reason: resolveMediaTranscodeFallbackReason(error),
      profile,
      durationMs: profile?.durationMs ?? null,
      error
    });
  }
}

/**
 * Executes transcode pipeline with abort + timeout controls.
 */
async function executeWithTimeout<T>(
  execute: (signal: AbortSignal) => Promise<T>,
  params: {
    timeoutMs: number;
    externalSignal?: AbortSignal;
    runtime: Pick<TranscodeRuntime, 'scheduleTimeout' | 'clearScheduledTimeout'>;
  }
): Promise<T> {
  const controller = new AbortController();
  let timeoutTriggered = false;
  let externallyAborted = false;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const onExternalAbort = () => {
    externallyAborted = true;
    controller.abort();
  };

  if (params.externalSignal?.aborted) {
    throw new WebTryError('media_transcode_aborted');
  }

  if (params.externalSignal) {
    params.externalSignal.addEventListener('abort', onExternalAbort, { once: true });
  }

  timeoutId = params.runtime.scheduleTimeout(() => {
    timeoutTriggered = true;
    controller.abort();
  }, params.timeoutMs);

  try {
    return await execute(controller.signal);
  } catch (error) {
    if (timeoutTriggered) {
      throw new WebTryError('media_transcode_timeout');
    }

    if (externallyAborted || params.externalSignal?.aborted || controller.signal.aborted) {
      throw new WebTryError('media_transcode_aborted');
    }

    throw error;
  } finally {
    if (timeoutId !== null) {
      params.runtime.clearScheduledTimeout(timeoutId);
    }
    if (params.externalSignal) {
      params.externalSignal.removeEventListener('abort', onExternalAbort);
    }
  }
}

/**
 * Default WebCodecs demux+decode+encode pipeline implementation.
 */
async function executeWebCodecsTranscodePipeline(params: {
  file: File;
  config: TryClientAudioTranscodeConfig;
  signal: AbortSignal;
}): Promise<TranscodePipelineOutput> {
  assertNotAborted(params.signal);

  const dependencies = await loadTranscodeDependencies();

  const demuxer = new dependencies.WebDemuxer({
    wasmFilePath: resolveTryMediaDemuxerWasmPath()
  });

  let decoder: AudioDecoder | null = null;
  let reader: ReadableStreamDefaultReader<EncodedAudioChunk> | null = null;
  let decoderError: unknown = null;

  const mp3Pipeline = new StreamingMp3Pipeline({
    Mp3EncoderCtor: dependencies.Mp3Encoder,
    outputSampleRateHz: TryClientAudioTranscodeFormat.OUTPUT_SAMPLE_RATE_HZ,
    outputBitrateKbps: TryClientAudioTranscodeFormat.OUTPUT_BITRATE_KBPS,
    outputChannelCount: TryClientAudioTranscodeFormat.OUTPUT_CHANNEL_COUNT,
    maxOutputBytes: params.config.maxOutputBytes
  });

  try {
    try {
      await demuxer.load(params.file);
    } catch (error) {
      throw new WebTryError('media_transcode_demux_failed', { details: error });
    }

    assertNotAborted(params.signal);

    let decoderConfig: AudioDecoderConfig;
    try {
      decoderConfig = await demuxer.getDecoderConfig('audio');
    } catch (error) {
      throw new WebTryError('media_transcode_demux_failed', { details: error });
    }

    let supportResult: DecoderSupportResult;
    try {
      supportResult = await AudioDecoder.isConfigSupported(decoderConfig) as DecoderSupportResult;
    } catch (error) {
      throw new WebTryError('media_transcode_unsupported_codec', { details: error });
    }

    if (!supportResult.supported) {
      throw new WebTryError('media_transcode_unsupported_codec', { details: decoderConfig });
    }

    decoder = new AudioDecoder({
      output: (audioData) => {
        try {
          mp3Pipeline.pushAudioData(audioData);
        } catch (error) {
          decoderError = error;
        } finally {
          try {
            audioData.close();
          } catch {
            // best-effort close
          }
        }
      },
      error: (error) => {
        decoderError = new WebTryError('media_transcode_decode_failed', { details: error });
      }
    });

    try {
      decoder.configure(supportResult.config);
    } catch (error) {
      throw new WebTryError('media_transcode_unsupported_codec', { details: error });
    }

    const chunkStream = demuxer.read('audio');
    reader = chunkStream.getReader();

    while (true) {
      assertNotAborted(params.signal);

      if (decoderError) {
        throw decoderError;
      }

      // reader is guaranteed non-null here: assigned at line 404 before the loop.
      const readResult = await reader!.read();
      if (readResult.done) {
        break;
      }

      try {
        decoder.decode(readResult.value);
      } catch (error) {
        throw new WebTryError('media_transcode_decode_failed', { details: error });
      }

      await waitForDecoderQueueDrain(decoder, params.signal);
    }

    await decoder.flush();
    if (decoderError) {
      throw decoderError;
    }

    const blob = mp3Pipeline.finish();
    return {
      blob,
      durationMs: mp3Pipeline.getOutputDurationMs()
    };
  } catch (error) {
    if (error instanceof WebTryError) {
      throw error;
    }

    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new WebTryError('media_transcode_aborted', { details: error });
    }

    throw new WebTryError('media_transcode_decode_failed', { details: error });
  } finally {
    try {
      reader?.releaseLock();
    } catch {
      // best-effort cleanup only
    }

    try {
      decoder?.close();
    } catch {
      // best-effort cleanup only
    }

    try {
      demuxer.destroy();
    } catch {
      // best-effort cleanup only
    }
  }
}

/**
 * Streaming MP3 pipeline that converts decoded AudioData to mono 16k MP3.
 */
class StreamingMp3Pipeline {
  private readonly targetSampleRateHz: number;
  private readonly maxOutputBytes: number;
  private readonly encoder: Mp3EncoderInstance;
  private readonly outputChunks: ArrayBuffer[] = [];
  private encodedBytes = 0;
  private totalOutputSamples = 0;
  private pendingSamples: Int16Array<ArrayBufferLike> = new Int16Array(0);
  private activeResampler: StreamingAverageResampler | null = null;

  constructor(params: {
    Mp3EncoderCtor: Mp3EncoderConstructor;
    outputSampleRateHz: number;
    outputBitrateKbps: number;
    outputChannelCount: number;
    maxOutputBytes: number;
  }) {
    this.targetSampleRateHz = params.outputSampleRateHz;
    this.maxOutputBytes = params.maxOutputBytes;
    this.encoder = new params.Mp3EncoderCtor(
      params.outputChannelCount,
      params.outputSampleRateHz,
      params.outputBitrateKbps
    );
  }

  /**
   * Accepts a decoded AudioData frame, downmixes to mono, resamples, and encodes to MP3.
   *
   * Lazily creates a StreamingAverageResampler on the first call, keyed to the
   * source sample rate. Throws if subsequent frames have a different sample rate.
   *
   * @param audioData - Decoded WebCodecs AudioData frame.
   */
  pushAudioData(audioData: AudioData): void {
    const mono = extractMonoPcmFloat32(audioData);

    if (!this.activeResampler) {
      this.activeResampler = new StreamingAverageResampler({
        inputSampleRateHz: audioData.sampleRate,
        outputSampleRateHz: this.targetSampleRateHz
      });
    }

    if (this.activeResampler.inputSampleRateHz !== audioData.sampleRate) {
      throw new WebTryError('media_transcode_decode_failed', {
        details: { expectedSampleRate: this.activeResampler.inputSampleRateHz, actualSampleRate: audioData.sampleRate }
      });
    }

    const resampled = this.activeResampler.process(mono);
    this.pushResampledSamples(resampled);
  }

  /**
   * Flushes remaining resampler tail and pending encoder samples, then returns the MP3 blob.
   *
   * Must be called exactly once after all pushAudioData() calls are complete.
   *
   * @returns Blob with MIME type `audio/mpeg` containing the encoded MP3 data.
   */
  finish(): Blob {
    if (this.activeResampler) {
      const tail = this.activeResampler.flush();
      this.pushResampledSamples(tail);
    }

    if (this.pendingSamples.length > 0) {
      const tailChunk = this.encoder.encodeBuffer(this.pendingSamples);
      this.pushEncodedChunk(tailChunk);
      this.pendingSamples = new Int16Array(0);
    }

    const finalChunk = this.encoder.flush();
    this.pushEncodedChunk(finalChunk);

    return new Blob(this.outputChunks, {
      type: TryClientAudioTranscodeFormat.OUTPUT_MIME_TYPE
    });
  }

  /**
   * Computes the output audio duration from total encoded samples and target sample rate.
   *
   * @returns Duration in milliseconds, or null if no samples were encoded.
   */
  getOutputDurationMs(): number | null {
    if (this.totalOutputSamples <= 0) {
      return null;
    }
    return Math.round((this.totalOutputSamples / this.targetSampleRateHz) * 1000);
  }

  /**
   * Converts resampled Float32 samples to Int16 and feeds MP3_FRAME_SAMPLES-sized
   * chunks to the lamejs encoder. Leftover samples are buffered in `pendingSamples`.
   *
   * @param samples - Resampled Float32 PCM at the target sample rate.
   */
  private pushResampledSamples(samples: Float32Array): void {
    if (samples.length === 0) {
      return;
    }

    this.totalOutputSamples += samples.length;

    const int16 = convertFloat32ToInt16(samples);
    if (this.pendingSamples.length === 0) {
      this.pendingSamples = cloneInt16(int16);
    } else {
      this.pendingSamples = concatInt16(this.pendingSamples, int16);
    }

    let offset = 0;
    while (offset + TranscodeQueueConfig.MP3_FRAME_SAMPLES <= this.pendingSamples.length) {
      const frame = this.pendingSamples.subarray(offset, offset + TranscodeQueueConfig.MP3_FRAME_SAMPLES);
      const encoded = this.encoder.encodeBuffer(frame);
      this.pushEncodedChunk(encoded);
      offset += TranscodeQueueConfig.MP3_FRAME_SAMPLES;
    }

    this.pendingSamples = cloneInt16(this.pendingSamples.subarray(offset));
  }

  /**
   * Appends an encoded MP3 chunk to the output buffer, enforcing the max output size.
   *
   * Clones the chunk to avoid holding references to encoder-internal buffers.
   * Throws WebTryError('media_transcode_output_too_large') if cumulative size exceeds limit.
   *
   * @param chunk - Encoded MP3 bytes from lamejs.
   */
  private pushEncodedChunk(chunk: Uint8Array): void {
    if (!(chunk instanceof Uint8Array) || chunk.length === 0) {
      return;
    }

    this.encodedBytes += chunk.length;
    if (this.encodedBytes > this.maxOutputBytes) {
      throw new WebTryError('media_transcode_output_too_large', {
        details: {
          encodedBytes: this.encodedBytes,
          maxOutputBytes: this.maxOutputBytes
        }
      });
    }

    const stableChunk = chunk.slice();
    const stableBuffer = stableChunk.buffer.slice(
      stableChunk.byteOffset,
      stableChunk.byteOffset + stableChunk.byteLength
    );
    this.outputChunks.push(stableBuffer);
  }
}

/**
 * Lightweight streaming resampler using weighted averaging.
 *
 * This keeps memory bounded (chunked processing) and works well for speech-focused
 * downsampling from common media sample rates to 16kHz.
 */
class StreamingAverageResampler {
  public readonly inputSampleRateHz: number;
  private readonly ratio: number;
  private tail = new Float32Array(0);
  private fractionalPosition = 0;

  constructor(params: { inputSampleRateHz: number; outputSampleRateHz: number }) {
    if (!Number.isFinite(params.inputSampleRateHz) || params.inputSampleRateHz <= 0) {
      throw new WebTryError('media_transcode_decode_failed', { details: 'invalid_input_sample_rate' });
    }

    this.inputSampleRateHz = params.inputSampleRateHz;
    this.ratio = params.inputSampleRateHz / params.outputSampleRateHz;

    if (!Number.isFinite(this.ratio) || this.ratio <= 0) {
      throw new WebTryError('media_transcode_decode_failed', { details: 'invalid_resample_ratio' });
    }
  }

  /**
   * Resamples a chunk of input Float32 PCM to the target sample rate using weighted averaging.
   *
   * Maintains internal state (tail buffer + fractional position) across calls so
   * the resampler works in streaming mode without requiring the full signal upfront.
   *
   * @param input - Next chunk of Float32 PCM at the source sample rate.
   * @returns Resampled Float32 PCM at the target sample rate.
   */
  process(input: Float32Array): Float32Array {
    if (input.length === 0) {
      return new Float32Array(0);
    }

    const merged = concatFloat32(this.tail, input);
    const estimatedLength = Math.max(0, Math.floor((merged.length - this.fractionalPosition) / this.ratio));
    const output = new Float32Array(estimatedLength);

    let outputIndex = 0;
    while (this.fractionalPosition + this.ratio <= merged.length) {
      const start = this.fractionalPosition;
      const end = start + this.ratio;
      output[outputIndex] = weightedAverage(merged, start, end);
      outputIndex += 1;
      this.fractionalPosition = end;
    }

    const consumed = Math.floor(this.fractionalPosition);
    this.tail = merged.slice(consumed);
    this.fractionalPosition -= consumed;

    return output.subarray(0, outputIndex);
  }

  /**
   * Emits one final output sample from remaining tail data if enough samples exist.
   *
   * Resets internal state. Call once after the last process() call.
   *
   * @returns Zero or one final resampled sample.
   */
  flush(): Float32Array {
    if (this.tail.length === 0) {
      return new Float32Array(0);
    }

    // If enough samples remain for one output sample, emit one final sample.
    if (this.fractionalPosition + this.ratio <= this.tail.length) {
      const value = weightedAverage(this.tail, this.fractionalPosition, this.fractionalPosition + this.ratio);
      this.tail = new Float32Array(0);
      this.fractionalPosition = 0;
      return new Float32Array([value]);
    }

    this.tail = new Float32Array(0);
    this.fractionalPosition = 0;
    return new Float32Array(0);
  }
}

/**
 * Converts a multi-channel AudioData frame into mono Float32 PCM by averaging channels.
 *
 * Uses the WebCodecs `copyTo()` API with `f32-planar` format to extract each channel,
 * then averages all channels sample-by-sample to produce a single mono signal.
 *
 * @param audioData - Decoded WebCodecs AudioData frame (any channel count).
 * @returns Mono Float32 PCM array with `audioData.numberOfFrames` samples.
 */
function extractMonoPcmFloat32(audioData: AudioData): Float32Array {
  const frameCount = audioData.numberOfFrames;
  const channelCount = Math.max(1, audioData.numberOfChannels);
  const mono = new Float32Array(frameCount);

  for (let channelIndex = 0; channelIndex < channelCount; channelIndex += 1) {
    const channelBuffer = new Float32Array(frameCount);
    audioData.copyTo(channelBuffer, {
      planeIndex: channelIndex,
      format: 'f32-planar'
    });

    for (let sampleIndex = 0; sampleIndex < frameCount; sampleIndex += 1) {
      mono[sampleIndex] += channelBuffer[sampleIndex];
    }
  }

  if (channelCount > 1) {
    for (let sampleIndex = 0; sampleIndex < frameCount; sampleIndex += 1) {
      mono[sampleIndex] /= channelCount;
    }
  }

  return mono;
}

/**
 * Waits until decoder queue drains below configured threshold.
 */
async function waitForDecoderQueueDrain(decoder: AudioDecoder, signal: AbortSignal): Promise<void> {
  while (decoder.decodeQueueSize > TranscodeQueueConfig.MAX_DECODE_QUEUE_SIZE) {
    assertNotAborted(signal);
    await sleep(TranscodeQueueConfig.BACKPRESSURE_WAIT_MS);
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * Computes a weighted average of samples over a fractional index range [start, end).
 *
 * Used by StreamingAverageResampler to produce a single output sample from a
 * fractional window of input samples. Boundary samples contribute proportionally.
 *
 * @param samples - Source Float32 PCM buffer.
 * @param start - Fractional start index (inclusive).
 * @param end - Fractional end index (exclusive).
 * @returns Weighted average sample value.
 */
function weightedAverage(samples: Float32Array, start: number, end: number): number {
  let totalWeight = 0;
  let weightedSum = 0;

  const startIndex = Math.floor(start);
  const endIndex = Math.ceil(end);

  for (let index = startIndex; index < endIndex; index += 1) {
    const left = Math.max(start, index);
    const right = Math.min(end, index + 1);
    const weight = right - left;
    if (weight <= 0) {
      continue;
    }

    const sample = index >= 0 && index < samples.length ? samples[index] : 0;
    weightedSum += sample * weight;
    totalWeight += weight;
  }

  return totalWeight > 0 ? (weightedSum / totalWeight) : 0;
}

/**
 * Converts Float32 PCM samples (range [-1.0, 1.0]) to Int16 PCM.
 *
 * Clamps input to [-1, 1] before scaling. Uses asymmetric scale factors
 * because Int16 range is [-32768, 32767] (not symmetric around zero).
 *
 * @param samples - Float32 PCM audio samples.
 * @returns Int16 PCM audio samples.
 */
function convertFloat32ToInt16(samples: Float32Array): Int16Array {
  const int16 = new Int16Array(samples.length);
  for (let index = 0; index < samples.length; index += 1) {
    const clamped = Math.max(-1, Math.min(1, samples[index]));
    int16[index] = clamped < 0
      ? Math.round(clamped * Int16ScaleFactors.NEGATIVE)
      : Math.round(clamped * Int16ScaleFactors.POSITIVE);
  }
  return int16;
}

/**
 * Concatenates two Int16Arrays into a new contiguous Int16Array.
 *
 * Returns the non-empty input directly when the other is empty (zero-copy fast path).
 *
 * @param first - First Int16Array segment.
 * @param second - Second Int16Array segment.
 * @returns New Int16Array containing first followed by second.
 */
function concatInt16(first: Int16Array<ArrayBufferLike>, second: Int16Array<ArrayBufferLike>): Int16Array<ArrayBufferLike> {
  if (first.length === 0) {
    return second;
  }
  if (second.length === 0) {
    return first;
  }

  const merged = new Int16Array(first.length + second.length);
  merged.set(first, 0);
  merged.set(second, first.length);
  return merged;
}

/**
 * Creates an independent copy of an Int16Array (detached from the source buffer).
 *
 * @param source - Int16Array to clone.
 * @returns New Int16Array with identical content.
 */
function cloneInt16(source: Int16Array<ArrayBufferLike>): Int16Array<ArrayBufferLike> {
  const clone = new Int16Array(source.length);
  clone.set(source, 0);
  return clone;
}

/**
 * Concatenates two Float32Arrays into a new contiguous Float32Array.
 *
 * Returns the non-empty input directly when the other is empty (zero-copy fast path).
 *
 * @param first - First Float32Array segment.
 * @param second - Second Float32Array segment.
 * @returns New Float32Array containing first followed by second.
 */
function concatFloat32(first: Float32Array, second: Float32Array): Float32Array {
  if (first.length === 0) {
    return second;
  }
  if (second.length === 0) {
    return first;
  }

  const merged = new Float32Array(first.length + second.length);
  merged.set(first, 0);
  merged.set(second, first.length);
  return merged;
}

/**
 * Throws a DOMException AbortError if the signal is already aborted.
 *
 * Used as a checkpoint in async loops to exit early on cancellation.
 *
 * @param signal - AbortSignal to check.
 * @throws {DOMException} AbortError when signal.aborted is true.
 */
function assertNotAborted(signal: AbortSignal): void {
  if (signal.aborted) {
    throw new DOMException('Aborted', 'AbortError');
  }
}

/**
 * Checks whether the browser supports the WebCodecs APIs needed for client-side transcoding.
 *
 * Required APIs: AudioDecoder, AudioData, EncodedAudioChunk, AbortController, ReadableStream.
 *
 * @returns True if all required APIs are available in the current environment.
 */
function isClientTranscodeSupported(): boolean {
  return typeof window !== 'undefined'
    && typeof AudioDecoder !== 'undefined'
    && typeof AudioData !== 'undefined'
    && typeof EncodedAudioChunk !== 'undefined'
    && typeof AbortController !== 'undefined'
    && typeof ReadableStream !== 'undefined';
}

/**
 * Derives an MP3 output filename from the source file name by replacing the extension.
 *
 * @param fileName - Original media file name (e.g., "interview.wav").
 * @returns Filename with `.mp3` extension (e.g., "interview.mp3").
 */
function buildTranscodedFileName(fileName: string): string {
  const normalized = (fileName || 'media').replace(/\.[a-zA-Z0-9]+$/, '');
  return `${normalized}.mp3`;
}

/**
 * Reads the SSR-injected media transcode config from `window.__GIST_TRY_MEDIA_CONFIG__`.
 *
 * Returns null in non-browser environments (SSR / Node).
 *
 * @returns Bootstrap media config or null.
 */
function readWindowTryMediaConfig(): TryMediaBootstrapConfig | null {
  if (typeof window === 'undefined') {
    return null;
  }
  return window.__GIST_TRY_MEDIA_CONFIG__ ?? null;
}

/**
 * Constructs a typed TryMediaTranscodeFallback result for non-throwing error paths.
 *
 * @param params - Fallback reason, optional profile/duration, and the caught error.
 * @returns Structured fallback result so callers can route to server extraction.
 */
function buildFallback(params: {
  reason: TryMediaTranscodeFallbackReason;
  profile: MediaFileProfile | null;
  durationMs: number | null;
  error: unknown;
}): TryMediaTranscodeFallback {
  return {
    status: 'fallback',
    transcodedFile: null,
    fallbackReason: params.reason,
    profile: params.profile,
    byteAccounting: null,
    durationMs: params.durationMs,
    errorMessage: resolveErrorMessage(params.error)
  };
}

/**
 * Extracts a human-readable message from an unknown error value.
 *
 * Prioritizes WebTryError.message, then Error.message, then String coercion.
 * Returns null for falsy values.
 *
 * @param error - Caught error of any type.
 * @returns Error message string, or null if error is falsy.
 */
function resolveErrorMessage(error: unknown): string | null {
  if (!error) {
    return null;
  }

  if (error instanceof WebTryError) {
    return error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return String(error);
}

type DecoderSupportResult = {
  supported?: boolean;
  config: AudioDecoderConfig;
};
